/**
 * Clients analysis — ported from data-crunch/analyze_clients.py
 *
 * Conversion time distribution: months between "Date de premier contact"
 * and "Date d'installation", grouped by cohort year (July 2023+).
 */

const EXCLUDED_GROUPS = new Set(['Wyes Internal Affairs']);

const MIN_CONTACT_DATE = new Date(2023, 6, 1); // July 2023

const COHORT_COLORS = {
    '2023': 'rgba(107,114,128,.7)',
    '2024': 'rgba(245,158,11,.7)',
    '2025': 'rgba(16,185,129,.7)',
    '2026': 'rgba(239,68,68,.7)',
};
const DEFAULT_COLOR = 'rgba(59,130,246,.7)';

const TYPE_COLORS = {
    'Particulier': 'rgba(99,102,241,.7)',
    'Pro': 'rgba(245,158,11,.7)',
};

function parseDate(s) {
    if (!s) return null;
    const trimmed = s.trim();
    if (!trimmed) return null;
    const parts = trimmed.split('-');
    if (parts.length !== 3) return null;
    const [y, m, d] = parts.map(Number);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m - 1, d);
}

function monthsBetween(d1, d2) {
    return (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
}

function monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

const MIN_INSTALL_MONTH = '2023-07';

function fillMonths(keys) {
    if (keys.length === 0) return [];
    const sorted = [...keys].sort();
    const [minY, minM] = sorted[0].split('-').map(Number);
    const [maxY, maxM] = sorted[sorted.length - 1].split('-').map(Number);
    const result = [];
    let y = minY, m = minM;
    while (y < maxY || (y === maxY && m <= maxM)) {
        result.push(`${y}-${String(m).padStart(2, '0')}`);
        m++;
        if (m > 12) { m = 1; y++; }
    }
    return result;
}

/**
 * Analyze parsed CLIENTS CSV rows.
 * Returns conversion time distribution for recent cohorts (July 2023+).
 */
export function analyzeClients(rows) {
    const conversions = []; // { name, cohort, months }
    let skippedNoContact = 0;
    let skippedNoInstall = 0;
    let skippedBoth = 0;
    let totalRows = 0;

    for (const row of rows) {
        const group = (row['Groupe'] || '').trim();
        if (EXCLUDED_GROUPS.has(group)) continue;

        const name = (row['Nom'] || '').trim();
        if (!name) continue;

        totalRows++;

        const dContact = parseDate(row['Date de premier contact'] || '');
        const dInstall = parseDate(row["Date d'installation"] || '');

        if (!dContact && !dInstall) {
            skippedBoth++;
            continue;
        }
        if (!dContact) {
            skippedNoContact++;
            continue;
        }
        if (!dInstall) {
            skippedNoInstall++;
            continue;
        }

        const rawType = (row['Type'] || '').trim();
        const clientType = rawType === 'Particulier' ? 'Particulier' : 'Pro';
        const cohort = String(dContact.getFullYear());
        const m = monthsBetween(dContact, dInstall);
        conversions.push({ name, cohort, months: m, contactDate: dContact, clientType });
    }

    // Filter to recent cohorts (contact date >= July 2023)
    const recent = conversions.filter(c => c.contactDate >= MIN_CONTACT_DATE);
    const cohorts = [...new Set(recent.map(c => c.cohort))].sort();
    const maxMonths = recent.length > 0 ? Math.max(...recent.map(c => c.months)) : 0;

    // Build distribution per cohort
    const dist = {};
    for (const cohort of cohorts) dist[cohort] = {};
    for (const { cohort, months } of recent) {
        dist[cohort][months] = (dist[cohort][months] || 0) + 1;
    }

    // Chart datasets
    const labels = Array.from({ length: maxMonths + 1 }, (_, i) => String(i));
    const datasets = cohorts.map(cohort => ({
        label: cohort,
        data: Array.from({ length: maxMonths + 1 }, (_, i) => dist[cohort][i] || 0),
        backgroundColor: COHORT_COLORS[cohort] || DEFAULT_COLOR,
        borderRadius: 4,
    }));

    // KPIs
    const recentMonths = recent.map(c => c.months).sort((a, b) => a - b);
    const total = recentMonths.length;
    const median = total > 0 ? recentMonths[Math.floor(total / 2)] : 0;
    const mean = total > 0
        ? Math.round(recentMonths.reduce((a, b) => a + b, 0) / total * 10) / 10
        : 0;

    // --- By type (Particulier vs Pro) ---

    const TYPE_ORDER = ['Particulier', 'Pro'];
    const typeDist = {};
    for (const t of TYPE_ORDER) typeDist[t] = {};
    for (const { clientType, months } of recent) {
        typeDist[clientType][months] = (typeDist[clientType][months] || 0) + 1;
    }

    const typeLabels = Array.from({ length: maxMonths + 1 }, (_, i) => String(i));
    const typeDatasets = TYPE_ORDER.map(t => ({
        label: t,
        data: Array.from({ length: maxMonths + 1 }, (_, i) => typeDist[t][i] || 0),
        backgroundColor: TYPE_COLORS[t],
        borderRadius: 4,
    }));

    const typeKpis = {};
    for (const t of TYPE_ORDER) {
        const vals = recent.filter(c => c.clientType === t).map(c => c.months).sort((a, b) => a - b);
        const n = vals.length;
        typeKpis[t] = {
            total: n,
            median: n > 0 ? vals[Math.floor(n / 2)] : 0,
            mean: n > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / n * 10) / 10 : 0,
        };
    }

    // --- Installations by month (by type, from 2023-07) ---

    const installByType = {};
    for (const t of TYPE_ORDER) installByType[t] = {};

    for (const row of rows) {
        const group = (row['Groupe'] || '').trim();
        if (EXCLUDED_GROUPS.has(group)) continue;
        const name = (row['Nom'] || '').trim();
        if (!name) continue;

        const dInstall = parseDate(row["Date d'installation"] || '');
        if (!dInstall) continue;

        const mk = monthKey(dInstall);
        if (mk < MIN_INSTALL_MONTH) continue;

        const rawType = (row['Type'] || '').trim();
        const clientType = rawType === 'Particulier' ? 'Particulier' : 'Pro';
        installByType[clientType][mk] = (installByType[clientType][mk] || 0) + 1;
    }

    const allInstallMonthKeys = new Set();
    for (const t of TYPE_ORDER) {
        for (const mk of Object.keys(installByType[t])) allInstallMonthKeys.add(mk);
    }
    const installMonths = fillMonths([...allInstallMonthKeys]);

    const installDatasets = TYPE_ORDER.map(t => ({
        label: t,
        data: installMonths.map(m => installByType[t][m] || 0),
        backgroundColor: TYPE_COLORS[t],
        borderRadius: 4,
    }));

    const installTotal = installMonths.reduce((sum, m) =>
        sum + TYPE_ORDER.reduce((s, t) => s + (installByType[t][m] || 0), 0), 0);

    // Skip summary
    const skippedTotal = skippedNoContact + skippedNoInstall + skippedBoth;
    const skipParts = [];
    if (skippedNoContact) skipParts.push(`${skippedNoContact} sans date de premier contact`);
    if (skippedNoInstall) skipParts.push(`${skippedNoInstall} sans date d'installation`);
    if (skippedBoth) skipParts.push(`${skippedBoth} sans aucune date`);

    return {
        labels,
        datasets,
        kpis: { total, median, mean },
        byType: {
            labels: typeLabels,
            datasets: typeDatasets,
            kpis: typeKpis,
        },
        installations: {
            labels: installMonths,
            datasets: installDatasets,
            total: installTotal,
        },
        skipParts,
        skippedTotal,
        totalRows,
    };
}
